import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import React, { useEffect, useMemo } from "react";
import { createPath, useNavigate } from "react-router-dom";
import { DataKind, DataKindKeys, SpreadsheetState, usePageState } from ".";
import { useAppState } from "../../app/useAppState";

import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
} from "@vrf/fr-vrf-core-react";
import {
  FrVrfButton,
  FrVrfButtonPanel,
  FrVrfFooter,
  FrVrfTextDisplayField,
} from "@vrf/fr-vrf-web-components-react";
import { DataKindMaintenancePageInitialState } from ".";
import { ApplicationMessageScreen } from "../../messages/screen-message";
import {
  GetDataKind,
  PutDataKind,
  useGetDataKind,
  usePutDataKind,
} from "../../services/data-kinds";
import { useGetGeneralCodes } from "../../services/data-list";
import {
  DataKindRequest,
  useCreateDeploymentRequestDataKindNames,
} from "../../services/deployment-request";
import { InDefCommonFlagType } from "../../util/general-code/enums/general-codes";
import {
  INIT_ERROR_RESULT_DATA,
  clearBackenddMessages,
  mappingErrorResponseToSpreadsheet,
} from "../../util/spreadsheet-data-utils";
import { ENV } from "@vrf/fr-vrf-core";
import _ from "lodash";
import { Cell } from "@vrf/fr-vrf-spreadsheet-core-templating";

export const DataKindMaintenancePageContent = () => {
  const { showToast, showConfirmationDialog, resetUserError } =
    useGlobalNotifier();
  const { messageForScreen } = useMessage();

  const [_appState, setAppState] = useAppState();
  const [pageState, setPageState] = usePageState();
  const { dataKindList } = pageState.searchResult;

  const [getGeneralCodesResponse, sendGetGeneralCodesRequest] =
    useGetGeneralCodes();
  const [getDataKindRequest, sendGetDataKindRequest] = useGetDataKind();
  const [putDataKindRequest, sendPutDataKindRequest] = usePutDataKind();
  const [
    createDeploymentRequestDataKindNamesRequest,
    sendCreateDeploymentRequestDataKindNamesRequest,
  ] = useCreateDeploymentRequestDataKindNames();
  const navigate = useNavigate();

  const TEMPLATE_FILE_URL = "spreadsheet-template/data-kind-maintenance.xlsx";
  const isNotEmpty = (str: string | null | undefined): boolean => {
    return str !== null && str !== undefined && str.trim().length > 0;
  };
  const onInitialDisplayed = () => {
    resetUserError();
    setAppState((prevState) => ({
      ...prevState,
      title: "Data Kind Maintenance",
    }));
    sendGetGeneralCodesRequest({ cdTypes: ["in_def_common_flag_type"] });
    sendGetDataKindRequest({
      dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
    });
  };

  const onGetGeneralCodesRequest = () => {
    if (!getGeneralCodesResponse) return;
    const inDefCommonFlagType =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === "in_def_common_flag_type",
      )?.cdList ?? [];

    setPageState((prevState) => ({
      ...prevState,
      dataList: {
        ...prevState.dataList,
        inDefCommonFlagType,
      },
    }));
  };

  const onGetDataKindResponded = () => {
    if (!getDataKindRequest) {
      return;
    }
    const dataKindList = [] as DataKind[];
    getDataKindRequest.dataKindList.forEach((e, idx) => {
      const dataKind = {} as DataKind;
      dataKind.addFlg = InDefCommonFlagType.NO;
      dataKind.displayDeployCheckbox =
        !!e.dtKindNameWtngChngEng && !!e.dtKindNameWtngChngLclLang;
      dataKind.deployFlg = false;
      dataKind.getDataKind = e;
      dataKind.errorColumnList = [];
      dataKind.errorMessage = "";
      dataKind.messageList = [];
      dataKindList.push(dataKind);
    });
    setPageState((prevState) => ({
      ...prevState,
      isSaved: true,
      searchResult: {
        ...prevState.searchResult,
        dataKindList: dataKindList,
      },
    }));
  };

  const onPutDataKindResponded = () => {
    if (!putDataKindRequest) return;

    if (Array.isArray(putDataKindRequest)) {
      const currentrecords: DataKind[] = spreadsheetState
        ? [...spreadsheetState.dataKindList]
        : [];

      const currentrecordsWithoutBackendError = currentrecords.map((row) =>
        clearBackenddMessages(row),
      );
      const newSpreadsheetRecordData: DataKind[] =
        mappingErrorResponseToSpreadsheet(
          putDataKindRequest,
          currentrecordsWithoutBackendError,
          DataKindMaintenancePageInitialState.displayColumn,
        );

      if (!spreadsheetState) return;
      setSpreadsheetState({
        ...spreadsheetState,
        dataKindList: newSpreadsheetRecordData,
      });
    } else {
      showToast({
        design: "completion",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
      });
      sendGetDataKindRequest({
        dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
      });
    }
  };

  const onCreateDeploymentRequestDataKindNamesResponded = () => {
    if (!createDeploymentRequestDataKindNamesRequest) return;

    if (
      !!createDeploymentRequestDataKindNamesRequest.warningMessage &&
      createDeploymentRequestDataKindNamesRequest.warningMessage.length > 0
    ) {
      showConfirmationDialog({
        type: "warning",
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00045),
        onOkButtonClicked: () => {
          const dataKindIdList = [] as DataKindRequest[];
          spreadsheetState?.dataKindList.forEach((e, idx) => {
            if (e.deployFlg) {
              dataKindIdList.push({
                index: idx,
                dtKindId: e.getDataKind.dtKindId,
              });
            }
          });
          sendCreateDeploymentRequestDataKindNamesRequest({
            dataKindIdList: dataKindIdList,
            forceStgDplyFlg: true,
            dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
          });
        },
      });
      return;
    }

    if (Array.isArray(createDeploymentRequestDataKindNamesRequest)) {
      const currentrecords: DataKind[] = spreadsheetState
        ? [...spreadsheetState.dataKindList]
        : [];

      const currentrecordsWithoutBackendError = currentrecords.map((row) =>
        clearBackenddMessages(row),
      );
      const newSpreadsheetRecordData: DataKind[] =
        mappingErrorResponseToSpreadsheet(
          createDeploymentRequestDataKindNamesRequest,
          currentrecordsWithoutBackendError,
          DataKindMaintenancePageInitialState.displayColumn,
        );

      if (!spreadsheetState) return;
      setSpreadsheetState({
        ...spreadsheetState,
        dataKindList: newSpreadsheetRecordData,
      });
      return;
    }

    showToast({
      design: "completion",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
    });
    sendGetDataKindRequest({
      dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
    });
  };

  useEffect(onInitialDisplayed, []);
  useEffect(onGetGeneralCodesRequest, [getGeneralCodesResponse]);
  useEffect(onGetDataKindResponded, [getDataKindRequest]);
  useEffect(onPutDataKindResponded, [putDataKindRequest]);
  useEffect(onCreateDeploymentRequestDataKindNamesResponded, [
    createDeploymentRequestDataKindNamesRequest,
  ]);

  const [spreadsheetState, setSpreadsheetState, initSpreadsheetState] =
    useSpreadsheetState<SpreadsheetState>();

  const spreadsheetInitialData = useMemo(() => {
    return {
      dataKindList: dataKindList,
      dataList: pageState.dataList,
    };
  }, [dataKindList, pageState.dataList]);

  const transition = (path: string) => {
    const href = createPath({
      pathname: `${path}`,
    });
    navigate(href);
  };

  const isDataKindKey = (arg: any): arg is keyof DataKind => {
    const dtKindKeys: (keyof GetDataKind)[] = [
      "dtKindId",
      "delFlg",
      "mltcstDsbldFlg",
      "dtKindNameLclLang",
      "dtKindNameEng",
      "dtKindNameWtngChngLclLang",
      "dtKindNameWtngChngEng",
    ];
    return dtKindKeys.includes(arg);
  };

  const onEditSpreadsheet = (event: CustomEvent) => {
    if (event.detail.name === "on-cell-edited") return;
    const rowIndex: number = event.detail.cell.data.index;
    const value: string = event.detail.cell.value ?? "";
    const eventName: string = event.detail.name;

    if (isDataKindKey(eventName)) {
      const isChanged = event.detail.cell.row.isChanged;
      const copyDataKindList = JSON.parse(
        JSON.stringify(spreadsheetState?.dataKindList),
      );
      copyDataKindList[rowIndex]["getDataKind"][eventName] = value;
      copyDataKindList[rowIndex]["isEdited"] = isChanged;
      const newState = JSON.parse(JSON.stringify(spreadsheetState));
      newState.dataKindList = copyDataKindList;
      setSpreadsheetState(newState);
      let isSaved = true;
      newState.dataKindList.forEach((e: DataKind) => {
        if (e.isEdited) {
          isSaved = false;
        }
      });
      setPageState((prevState) => ({ ...prevState, isSaved: isSaved }));
    } else if (eventName === "deployFlg") {
      const newState: SpreadsheetState = JSON.parse(
        JSON.stringify(spreadsheetState),
      );
      newState.dataKindList[rowIndex].deployFlg = stringToBoolean(value);
      setSpreadsheetState(newState);
    }
  };

  const onCellBulkEdited = (event: CustomEvent) => {
    if (!spreadsheetState) return;
    const newPicAddressList = _.cloneDeep(
      spreadsheetState?.dataKindList,
    ) as DataKind[];

    event.detail.affectedCells.cells.forEach((item: Cell) => {
      const value: string = item.value;
      const rowIdx: number = item.data.index;
      const colName: DataKindKeys = event.detail.name as DataKindKeys;
      newPicAddressList[rowIdx].getDataKind[colName] = value;
      newPicAddressList[rowIdx].isEdited = item.row.isChanged;
    });

    let isSaved = true;
    newPicAddressList.forEach((e: DataKind) => {
      if (e.isEdited) {
        isSaved = false;
      }
    });

    setSpreadsheetState({
      ...spreadsheetState,
      dataKindList: newPicAddressList,
    });
    setPageState((prevState) => ({ ...prevState, isSaved: isSaved }));
  };

  const stringToBoolean = (value: string | null | undefined): boolean => {
    if (!value) {
      return false;
    }
    return value.toLowerCase() === "true" || value === "1";
  };

  /**
   * Add Row Button Clicked.
   * @param event that is passed from spreadsheet.
   */
  const onAddRowClicked = () => {
    if (!spreadsheetState) return;
    const insertRowIndex: number =
      (pageState.spreadsheetSelectedRowIndex ?? 0) + 1;

    const newSpreadsheetState = spreadsheetState
      ? [...spreadsheetState.dataKindList]
      : [];
    const addSpreadsheetRow: DataKind = {
      ...INIT_ERROR_RESULT_DATA,
      addFlg: InDefCommonFlagType.YES,
      isEdited: true,
      displayDeployCheckbox: false,
      deployFlg: false,
      getDataKind: {
        delFlg: InDefCommonFlagType.NO,
        dtKindId: "",
        mltcstDsbldFlg: InDefCommonFlagType.NO,
        dtKindNameLclLang: "",
        dtKindNameEng: "",
        dtKindNameWtngChngLclLang: "",
        dtKindNameWtngChngEng: "",
        updateDateTime: "",
        deployedInEdhFlg: "",
      },
    };
    newSpreadsheetState.splice(insertRowIndex, 0, addSpreadsheetRow);
    setSpreadsheetState({
      ...spreadsheetState,
      dataKindList: [...newSpreadsheetState],
    });

    setPageState((prevState) => ({ ...prevState, isSaved: false }));
  };

  /**
   * Spreadsheet cell selected.
   * @param event that is passed from spreadsheet.
   */
  const onCellSelected = (event: CustomEvent) => {
    const rowIndex: number = event.detail.cell?.data.index;
    setPageState((prevState) => ({
      ...prevState,
      spreadsheetSelectedRowIndex: rowIndex,
    }));
  };

  /**
   * Spreadsheet delete icon clicked.
   * @param event that is passed from spreadsheet.
   */
  const onDeleteIconClicked = (event: CustomEvent) => {
    if (!spreadsheetState) return;
    if (event.detail.name !== "deleteIcon") return;
    const newSpreadsheetState: DataKind[] = spreadsheetState
      ? [...spreadsheetState.dataKindList]
      : [];
    const rowIndex: number = event.detail.cell?.data.index;
    newSpreadsheetState.splice(rowIndex, 1);

    setSpreadsheetState({
      ...spreadsheetState,
      dataKindList: newSpreadsheetState,
    });
  };

  /**
   * Footer
   */
  const FooterContent: React.FC = () => {
    const fileLayoutButtonClickedHandle = () => {
      if (!pageState.isSaved) {
        showConfirmationDialog({
          type: "confirm",
          title: "",
          message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
          onOkButtonClicked: () => {
            transition(`/file-layout-maintenance`);
          },
        });
      } else {
        transition(`/file-layout-maintenance`);
      }
    };

    const applyChangedDataKindNameButtonClickedHandle = () => {
      if (!spreadsheetState?.dataKindList) {
        return;
      }

      const dataKindList = [] as PutDataKind[];
      const newSpreadsheetRecordData = [] as DataKind[];
      let isCheckboxError = false;
      for (let idx = 0; idx < spreadsheetState?.dataKindList.length; idx++) {
        const e = spreadsheetState?.dataKindList[idx];
        if (e.deployFlg) {
          if (
            e.getDataKind.deployedInEdhFlg &&
            e.getDataKind.deployedInEdhFlg === InDefCommonFlagType.YES
          ) {
            e.errorMessage = messageForScreen(
              ApplicationMessageScreen.APP_SCN_00191,
            );
            isCheckboxError = true;
          }
          let dataKind = {} as PutDataKind;
          dataKind.delFlg = e.getDataKind.delFlg;
          dataKind.dtKindId = e.getDataKind.dtKindId;
          dataKind.mltcstDsbldFlg = e.getDataKind.mltcstDsbldFlg;
          if (isNotEmpty(e.getDataKind.dtKindNameWtngChngLclLang)) {
            dataKind.dtKindNameLclLang =
              e.getDataKind.dtKindNameWtngChngLclLang;
            dataKind.dtKindNameWtngChngLclLang = "";
          } else {
            dataKind.dtKindNameLclLang = e.getDataKind.dtKindNameLclLang;
          }
          if (isNotEmpty(e.getDataKind.dtKindNameWtngChngEng)) {
            dataKind.dtKindNameEng = e.getDataKind.dtKindNameWtngChngEng;
            dataKind.dtKindNameWtngChngEng = "";
          } else {
            dataKind.dtKindNameEng = e.getDataKind.dtKindNameEng;
          }
          dataKind.updateDateTime = e.getDataKind.updateDateTime;
          dataKind.index = idx;
          dataKindList.push(dataKind);
        }
        newSpreadsheetRecordData.push(e);
      }
      if (dataKindList.length === 0) {
        showToast({
          design: "alert",
          message: messageForScreen(ApplicationMessageScreen.APP_SCN_00035),
        });
        return;
      }

      if (isCheckboxError) {
        // show error message.
        setSpreadsheetState({
          ...spreadsheetState,
          dataKindList: newSpreadsheetRecordData,
        });
        showToast({
          design: "alert",
          message: messageForScreen(ApplicationMessageScreen.APP_SCN_00191),
        });
        return;
      }

      showConfirmationDialog({
        type: "confirm",
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00045),
        onOkButtonClicked: () => {
          resetUserError();
          sendPutDataKindRequest({
            dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
            dataKindList: dataKindList,
          });
        },
      });
    };

    const deployButtonClickedHandle = () => {
      if (!spreadsheetState?.dataKindList) {
        return;
      }
      const dataKindIdList = [] as DataKindRequest[];
      const newSpreadsheetRecordData = [] as DataKind[];
      let isCheckboxError = false;
      for (let idx = 0; idx < spreadsheetState?.dataKindList.length; idx++) {
        const e = spreadsheetState?.dataKindList[idx];
        if (e.deployFlg) {
          if (
            e.getDataKind.deployedInEdhFlg &&
            e.getDataKind.deployedInEdhFlg === InDefCommonFlagType.NO
          ) {
            e.errorMessage = messageForScreen(
              ApplicationMessageScreen.APP_SCN_00190,
            );
            isCheckboxError = true;
          }
          dataKindIdList.push({ index: idx, dtKindId: e.getDataKind.dtKindId });
        }
        newSpreadsheetRecordData.push(e);
      }
      if (dataKindIdList.length === 0) {
        showToast({
          design: "alert",
          message: messageForScreen(ApplicationMessageScreen.APP_SCN_00035),
        });
        return;
      }

      if (isCheckboxError) {
        // show error message.
        setSpreadsheetState({
          ...spreadsheetState,
          dataKindList: newSpreadsheetRecordData,
        });
        showToast({
          design: "alert",
          message: messageForScreen(ApplicationMessageScreen.APP_SCN_00190),
        });
        return;
      }

      showConfirmationDialog({
        type: "confirm",
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00045),
        onOkButtonClicked: () => {
          resetUserError();
          sendCreateDeploymentRequestDataKindNamesRequest({
            dataKindIdList: dataKindIdList,
            forceStgDplyFlg: false,
            dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
          });
        },
      });
    };
    const saveButtonClickedHandle = () => {
      showConfirmationDialog({
        type: "confirm",
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00045),
        onOkButtonClicked: () => {
          resetUserError();
          const dataKindList = [] as PutDataKind[];
          spreadsheetState?.dataKindList.forEach((e, idx) => {
            if (e.isEdited) {
              let dataKind = {} as PutDataKind;
              dataKind.delFlg = e.getDataKind.delFlg;
              dataKind.dtKindId = e.getDataKind.dtKindId;
              dataKind.mltcstDsbldFlg = e.getDataKind.mltcstDsbldFlg;
              dataKind.dtKindNameLclLang = e.getDataKind.dtKindNameLclLang;
              dataKind.dtKindNameEng = e.getDataKind.dtKindNameEng;
              dataKind.dtKindNameWtngChngLclLang =
                e.getDataKind.dtKindNameWtngChngLclLang;
              dataKind.dtKindNameWtngChngEng =
                e.getDataKind.dtKindNameWtngChngEng;
              dataKind.updateDateTime = e.getDataKind.updateDateTime;
              dataKind.index = idx;
              dataKindList.push(dataKind);
            }
          });
          sendPutDataKindRequest({
            dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
            dataKindList: dataKindList,
          });
        },
      });
    };
    return (
      <FrVrfFooter>
        {/* File Layout Button */}
        <FrVrfButton
          content="File Layout"
          theme="secondary"
          size="large"
          onClicked={fileLayoutButtonClickedHandle}
        />

        {/* Apply Changed DataKindName Button */}
        <FrVrfButton
          content="Apply Changed DataKindName"
          theme="primary"
          size="large"
          disabled={
            !pageState.isSaved ||
            !spreadsheetState?.dataKindList.some(
              (e) =>
                e.deployFlg &&
                e.getDataKind.deployedInEdhFlg &&
                e.getDataKind.deployedInEdhFlg === InDefCommonFlagType.NO,
            )
          }
          onClicked={applyChangedDataKindNameButtonClickedHandle}
          authActId="data_kind_maintenance.button.apply_changed_data_kind_name"
        />

        {/* Deploy DataKindName Button */}
        <FrVrfButton
          content="Deploy DataKindName"
          theme="primary"
          size="large"
          disabled={
            !pageState.isSaved ||
            !spreadsheetState?.dataKindList.some(
              (e) =>
                e.deployFlg &&
                e.getDataKind.deployedInEdhFlg &&
                e.getDataKind.deployedInEdhFlg === InDefCommonFlagType.YES,
            )
          }
          onClicked={deployButtonClickedHandle}
          authActId="data_kind_maintenance.button.deploy_data_kind_name"
        />
        {/* Save Button */}
        <FrVrfButton
          content="Save"
          theme="primary"
          size="large"
          disabled={pageState.isSaved}
          onClicked={saveButtonClickedHandle}
          authActId="data_kind_maintenance.button.save"
        />
      </FrVrfFooter>
    );
  };

  return (
    <>
      <FrVrfTextDisplayField text="Data Kind Maintenance" />
      <FrVrfButtonPanel>
        <FrVrfButton
          content="Add Row"
          size="medium"
          theme="secondary"
          slot="left"
          onClicked={onAddRowClicked}
          authActId="data_kind_maintenance.button.add_row"
        />
      </FrVrfButtonPanel>
      <br />
      <FrVrfSpreadsheet
        data={spreadsheetInitialData}
        templateUrl={TEMPLATE_FILE_URL}
        onInitialized={initSpreadsheetState}
        onCellSelected={onCellSelected}
        onCellClicked={onDeleteIconClicked}
        onCellEdited={onEditSpreadsheet}
        onCellBulkEdited={onCellBulkEdited}
        maxHeight={800}
      />

      {/* Footer */}
      <FooterContent />
    </>
  );
};
